/**
 * @typedef {import('./PropertyState').default} State
 * @typedef {import('./PropertyFormValues').default} PropertyFormValues
 * @typedef {import('../../../shared/AuthProvider/State').default} AuthState
 * @typedef {Readonly<import('../PropertyProvider/PropertyContext').default>} PropertyContext
 * @typedef {import('./propertyCategories').PropertyCategory} PropertyCategory
 * @typedef {NonNullable<import('../../shared/PropertyService/EditablePropertyDetails').default['details']>} EditablePropertyFields
 * @typedef {import('../../shared/PropertyService/EditablePropertyDetails').default} EditablePropertyDetails
 * @typedef {Partial<import('../../../Auth/AuthService/AuthService').default>} AuthService
 * @typedef {{
 * 	readonly authService: AuthService;
 * 	readonly location?: {
 * 		readonly state?: {
 * 			readonly referrer?: string;
 * 		};
 * 	};
 * 	readonly match: {
 * 		readonly params: {
 * 			readonly propertyId: string;
 * 		};
 * 	};
 * 	readonly property: PropertyContext;
 * 	readonly propertyService: import('../../shared/PropertyService/PropertyService').default;
 * }} Props
 */
import { AuthContext } from 'shared/AuthProvider';
import { PROPERTY_CATEGORIES } from './propertyCategories';
import { file as validateFile } from 'shared/validators';
import { Redirect } from 'react-router-dom';
import { translate } from 'App/Internationalization';
import { withProperty } from 'App/Homeowner/PropertyProvider';
import { withPropertyService } from 'service-container';
import * as propertyService from 'App/Homeowner/Properties/property-service';
import captureError from 'utils/captureError';
import Loading from 'shared/Loading';
import mimeList from '@mooveguru/yhh-shared-config/files/allowed-mimes.json';
import paths from 'config/local/paths';
import PropertyForm from 'App/Homeowner/Properties/PropertyForm';
import React from 'react';
import InlineSVG from 'react-inlinesvg/esm';
import images from '../../../config/local/images';

const successMessage = translate('homeowner.pages.properties.success_message');
/**
 * @param {string | number | null | undefined} value
 * @returns {number | null}
 */
function toNumberOrNull(value) {
	if (value === null || value === undefined || value === '') {
		return null;
	}

	const parsedValue = Number(value);
	return Number.isFinite(parsedValue) ? parsedValue : null;
}

/**
 * @param {string | number | null | undefined} value
 * @returns {string | null}
 */
function toNullableString(value) {
	if (value === '' || value === undefined || value === null) {
		return null;
	}

	return String(value);
}

/**
 * @param {EditablePropertyFields} values
 * @returns {EditablePropertyFields}
 */
function buildPropertyDetails(values) {
	return {
		acreage: toNumberOrNull(values.acreage),
		amountToLoan: toNumberOrNull(values.amountToLoan),
		basement: toNullableString(values.basement),
		bedrooms: toNumberOrNull(values.bedrooms),
		buildYear: toNumberOrNull(values.buildYear),
		constructionType: toNullableString(values.constructionType),
		dueDate: toNullableString(values.dueDate),
		fireplace: toNullableString(values.fireplace),
		floorCover: toNullableString(values.floorCover),
		foundationType: toNullableString(values.foundationType),
		garageCars: toNumberOrNull(values.garageCars),
		garageType: toNullableString(values.garageType),
		heating: toNullableString(values.heating),
		heatingFuelType: toNullableString(values.heatingFuelType),
		interiorWalls: toNullableString(values.interiorWalls),
		lenderName: toNullableString(values.lenderName),
		loanType: toNullableString(values.loanType),
		lotSizeSquareFootage: toNumberOrNull(values.lotSizeSquareFootage),
		maximumInterestRate: toNumberOrNull(values.maximumInterestRate),
		numberOfBaths: toNumberOrNull(values.numberOfBaths),
		numberOfPartialBaths: toNumberOrNull(values.numberOfPartialBaths),
		originalDateOfContract: toNullableString(values.originalDateOfContract),
		otherRooms: toNumberOrNull(values.otherRooms),
		plumbingFixtures: toNullableString(values.plumbingFixtures),
		pool: toNullableString(values.pool),
		rate: toNumberOrNull(values.rate),
		roofCover: toNullableString(values.roofCover),
		roofType: toNullableString(values.roofType),
		sewer: toNullableString(values.sewer),
		stories: toNumberOrNull(values.stories),
		style: toNullableString(values.style),
		totalNumberOfRooms: toNumberOrNull(values.totalNumberOfRooms),
		water: toNullableString(values.water),
	};
}

/**
 * @param {string | null | undefined} value
 * @returns {PropertyCategory | null}
 */
function normalizeCategory(value) {
	if (!value) {
		return null;
	}

	const normalizedValue = String(value)
		.trim()
		.toLowerCase()
		.replace(/[\s'_-]+/g, '');

	/** @type {Record<string, PropertyCategory>} */
	const aliasMap = {
		formerhome: PROPERTY_CATEGORIES.FORMER_HOME,
		friendsorfamilyhome: PROPERTY_CATEGORIES.FRIENDS_OR_FAMILY_HOME,
		mywatchlist: PROPERTY_CATEGORIES.WATCH_LIST,
		primaryhome: PROPERTY_CATEGORIES.PRIMARY_HOME,
		secondaryhome: PROPERTY_CATEGORIES.SECOND_HOME,
		secondhome: PROPERTY_CATEGORIES.SECOND_HOME,
		watchlist: PROPERTY_CATEGORIES.WATCH_LIST,
	};

	return aliasMap[normalizedValue] ?? null;
}

/**
 * @returns {EditablePropertyFields}
 */
function getEmptyPropertyDetails() {
	return {
		acreage: null,
		amountToLoan: null,
		basement: null,
		bedrooms: null,
		buildYear: null,
		constructionType: null,
		dueDate: null,
		fireplace: null,
		floorCover: null,
		foundationType: null,
		garageCars: null,
		garageType: null,
		heating: null,
		heatingFuelType: null,
		interiorWalls: null,
		lenderName: null,
		loanType: null,
		lotSizeSquareFootage: null,
		maximumInterestRate: null,
		numberOfBaths: null,
		numberOfPartialBaths: null,
		originalDateOfContract: null,
		otherRooms: null,
		plumbingFixtures: null,
		pool: null,
		rate: null,
		roofCover: null,
		roofType: null,
		sewer: null,
		stories: null,
		style: null,
		totalNumberOfRooms: null,
		water: null,
	};
}

/**
 * @param {EditablePropertyDetails} property
 * @param {EditablePropertyFields} details
 * @param {PropertyFormValues['category']} category
 * @returns {PropertyFormValues}
 */
function buildInitialValues(property, details, category) {
	return {
		acreage: toNumberOrNull(details.acreage),
		amountToLoan: toNumberOrNull(details.amountToLoan),
		basement: details.basement,
		bedrooms: toNumberOrNull(details.bedrooms),
		buildYear: toNumberOrNull(details.buildYear),
		category,
		city: toNullableString(property.address.city),
		constructionType: details.constructionType,
		country: toNullableString(property.address.country),
		dueDate: details.dueDate,
		fireplace: details.fireplace,
		floorCover: details.floorCover,
		foundationType: details.foundationType,
		garageCars: toNumberOrNull(details.garageCars),
		garageType: details.garageType,
		heating: details.heating,
		heatingFuelType: details.heatingFuelType,
		interiorWalls: details.interiorWalls,
		isPrimary: false,
		lenderName: details.lenderName,
		loanType: details.loanType,
		lotSizeSquareFootage: toNumberOrNull(details.lotSizeSquareFootage),
		maximumInterestRate: toNumberOrNull(details.maximumInterestRate),
		numberOfBaths: toNumberOrNull(details.numberOfBaths),
		numberOfPartialBaths: toNumberOrNull(details.numberOfPartialBaths),
		originalDateOfContract: details.originalDateOfContract,
		otherRooms: toNumberOrNull(details.otherRooms),
		plumbingFixtures: details.plumbingFixtures,
		pool: details.pool,
		postalCode: toNullableString(property.address.postalCode),
		rate: toNumberOrNull(details.rate),
		roofCover: details.roofCover,
		roofType: details.roofType,
		sewer: details.sewer,
		state: toNullableString(property.address.state),
		stories: toNumberOrNull(details.stories),
		streetAddress1: toNullableString(property.address.streetAddress1),
		streetAddress2: toNullableString(property.address.streetAddress2),
		style: details.style,
		title: toNullableString(property.title),
		totalNumberOfRooms: toNumberOrNull(details.totalNumberOfRooms),
		water: details.water,
	};
}

/** @extends {React.Component<Props, State>} */
class EditProperty extends React.Component {
	/** @type {AuthState | null} */
	context = null;

	/**
	 * @param {Props} props
	 */
	constructor(props) {
		super(props);

		/** @type {State} */
		this.state = {
			editWasSuccessful: false,
			errorMessage: null,
			file: null,
			image: null,
			initialValues: {
				acreage: null,
				amountToLoan: null,
				basement: null,
				bedrooms: null,
				buildYear: null,
				category: null,
				city: null,
				constructionType: null,
				country: null,
				dueDate: null,
				fireplace: null,
				floorCover: null,
				foundationType: null,
				garageCars: null,
				garageType: null,
				heating: null,
				heatingFuelType: null,
				interiorWalls: null,
				isPrimary: false,
				lenderName: null,
				loanType: null,
				lotSizeSquareFootage: null,
				maximumInterestRate: null,
				numberOfBaths: null,
				numberOfPartialBaths: null,
				originalDateOfContract: null,
				otherRooms: null,
				plumbingFixtures: null,
				pool: null,
				postalCode: null,
				rate: null,
				roofCover: null,
				roofType: null,
				sewer: null,
				state: null,
				stories: null,
				streetAddress1: null,
				streetAddress2: null,
				style: null,
				title: null,
				totalNumberOfRooms: null,
				water: null,
			},
			isGettingData: true,
			property: {
				city: '',
				country: '',
				id: '',
				postalCode: '',
				state: '',
				streetAddress1: '',
				streetAddress2: '',
			},
		};

		this.handleDeleteImage = this.handleDeleteImage.bind(this);
		this.handleFileUploadChange = this.handleFileUploadChange.bind(this);
		this.handleSubmit = this.handleSubmit.bind(this);
	}

	async componentDidMount() {
		await this.getAndSetInitialValues();
	}

	async getAndSetInitialValues() {
		try {
			const propertyId = this.props.match.params.propertyId;
			const property =
				await this.props.propertyService.getEditablePropertyById(
					propertyId
				);
			const details = property.details ?? getEmptyPropertyDetails();
			const rawProperty = await this.props.propertyService
				.getPropertyById(propertyId)
				.catch(() => null);
			const propertyList = await this.props.propertyService
				.getHomeownerProperties()
				.catch(() => []);
			const matchedProperty = propertyList.find(
				(listProperty) => listProperty.id === propertyId
			);
			/** @type {PropertyFormValues['category']} */
			const resolvedCategory =
				normalizeCategory(property.category) ??
				normalizeCategory(rawProperty?.category) ??
				normalizeCategory(matchedProperty?.category);

			this.setState({
				errorMessage: null,
				image: property.image?.id ? property.image : null,
				initialValues: buildInitialValues(
					property,
					details,
					resolvedCategory
				),
				isGettingData: false,
			});
		} catch (error) {
			captureError(error);

			this.setState({
				errorMessage: translate('global.error'),
				isGettingData: false,
			});
		}
	}

	/**
	 * @param {React.ChangeEvent<HTMLInputElement>} event
	 */
	handleFileUploadChange(event) {
		if (!event.target.files?.length) {
			return;
		}
		this.setState({ file: event.target.files[0] });
	}

	/**
	 * @param {PropertyFormValues} values
	 */
	async handleSubmit(values) {
		const accessToken = this.#getAccessToken();

		if (!accessToken || !this.#validateSelectedImage()) {
			return;
		}

		try {
			await this.#updateProperty(values, accessToken);
			await this.#updatePropertyImage(accessToken);
			this.#invalidatePropertyImage();
			await this.#refreshSelectedProperty();
			this.#setSuccessState(values);
		} catch (error) {
			captureError(error);

			this.setState({
				errorMessage: translate('global.error'),
				isGettingData: false,
			});
		}
	}

	/** @returns {string | null} */
	#getAccessToken() {
		const accessToken = this.context?.accessToken;

		if (accessToken) {
			return accessToken;
		}

		this.setState({
			errorMessage: translate('global.error'),
			isGettingData: false,
		});

		return null;
	}

	/**
	 * @param {PropertyFormValues} values
	 * @param {string} accessToken
	 * @returns {Promise<void>}
	 */
	async #updateProperty(values, accessToken) {
		await propertyService.updateProperty(
			this.props.match.params.propertyId,
			this.#buildPropertyPayload(values),
			accessToken
		);
	}

	/**
	 * @param {string} accessToken
	 * @returns {Promise<void>}
	 */
	async #updatePropertyImage(accessToken) {
		const file = this.state.file;

		if (!file) {
			return;
		}

		await propertyService.updatePropertyImage(
			this.props.match.params.propertyId,
			'street-view',
			file,
			accessToken
		);
	}

	/** @returns {Promise<void>} */
	async #refreshSelectedProperty() {
		const propertyId = this.props.match.params.propertyId;

		if (!this.props.property?.setSelectedProperty || !propertyId) {
			return;
		}

		await this.props.property.setSelectedProperty(propertyId, true);
	}

	/** @returns {void} */
	#invalidatePropertyImage() {
		const propertyId = this.props.match.params.propertyId;

		if (!propertyId) {
			return;
		}

		this.props.propertyService.invalidateDefaultImage(propertyId);
	}

	#validateSelectedImage() {
		if (!this.state.file) {
			return true;
		}

		try {
			validateFile(this.state.file, mimeList.images, 'image');
			return true;
		} catch (error) {
			if (error instanceof Error) {
				captureError(error);
				this.setState({ errorMessage: translate('global.error') });
			}
		}

		return false;
	}

	/**
	 * @param {PropertyFormValues} values
	 * @returns {EditablePropertyDetails}
	 */
	#buildPropertyPayload(values) {
		const nullableCategory =
			values.category ?? this.state.initialValues.category ?? null;
		const nullableDetails = buildPropertyDetails(values);

		return {
			address: {
				city: toNullableString(values.city),
				country: toNullableString(values.country),
				postalCode: toNullableString(values.postalCode),
				state: toNullableString(values.state),
				streetAddress1: toNullableString(values.streetAddress1),
				streetAddress2: toNullableString(values.streetAddress2),
			},
			category: nullableCategory,
			details: nullableDetails,
			title: toNullableString(values.title),
		};
	}

	/**
	 * @param {PropertyFormValues} values
	 * @returns {void}
	 */
	#setSuccessState(values) {
		this.setState({
			editWasSuccessful: true,
			errorMessage: null,
			property: {
				city: values.city,
				country: values.country,
				id: this.props.match.params.propertyId,
				postalCode: values.postalCode,
				state: values.state,
				streetAddress1: values.streetAddress1,
				streetAddress2: values.streetAddress2,
			},
		});
	}

	async handleDeleteImage() {
		try {
			if (this.state.image) {
				await this.props.propertyService.deletePropertyImage(
					this.props.match.params.propertyId,
					this.state?.image?.id
				);
				this.#invalidatePropertyImage();
			}

			this.setState({
				errorMessage: null,
				image: null,
			});
		} catch (error) {
			if (error instanceof Error) {
				captureError(error);

				this.setState({
					errorMessage: translate('global.error'),
					isGettingData: false,
				});
			}
		}
	}

	render() {
		if (this.state.editWasSuccessful) {
			return (
				<Redirect
					to={{
						pathname: paths.app.homeowner.properties.validate,
						state: {
							message: successMessage,
							property: this.state.property,
						},
					}}
				/>
			);
		}

		if (this.state.isGettingData) {
			return <Loading />;
		}

		return (
			<React.Fragment>
				<PropertyForm
					cancelButtonPropertyId={this.props.match.params.propertyId}
					cancelButtonReferrerPath={
						this.props.location?.state?.referrer
					}
					errorMessage={this.state.errorMessage}
					headingContent={translate(
						'homeowner.pages.properties.form.heading'
					)}
					image={this.state.image?.path}
					initialValues={this.state.initialValues}
					onChange={this.handleFileUploadChange}
					onDeleteImage={this.handleDeleteImage}
					onSubmit={this.handleSubmit}
					submitButtonContent={translate(
						'homeowner.pages.properties.form.submit'
					)}
				/>

				<section className="mt-6  rich-text form-alert form-alert-grey w-fit">
					<p className="title">
						<i className="icon icon-xs">
							<InlineSVG src={images.icons.info} />
						</i>
						{translate('global.placeholder_form')}
					</p>
				</section>
			</React.Fragment>
		);
	}
}

EditProperty.contextType = AuthContext;

export default withProperty(withPropertyService(EditProperty));
